import React, { useState, useEffect, useMemo, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import {
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  Calendar,
  Check,
  ChevronDown,
  Code2,
  Mail,
  MessageCircle,
  PenTool,
  Rocket,
  ShieldCheck,
  Sparkles,
  Star,
} from "lucide-react";
import BrowserFrame, { useAddressBar } from "../components/web/BrowserFrame";
import Reveal, { EASE, MaskReveal } from "../components/web/Reveal";
import WebStyles from "../components/web/WebStyles";
import WebNav from "../components/web/WebNav";
import WorkShowcase from "../components/web/WorkShowcase";
import SiteShot from "../components/web/SiteShot";
import useIsDesktop from "../components/web/useIsDesktop";
import BuildSequence from "../components/web/BuildSequence";
import DomainMarquee from "../components/web/DomainMarquee";
import Footer from "../components/Footer";
import HubSpotForm from "../components/HubSpotForm";

/* ------------------------------------------------------------------ *
 * Content
 * ------------------------------------------------------------------ */

const pricingTiers = [
  {
    name: "Starter",
    price: "1,500",
    description: "Your website, domain, email, and calendar - set up and ready to go.",
    highlights: [
      "Custom website (1-5 pages)",
      "Mobile-friendly",
      "Contact form & enquiry routing",
      "Dedicated human support",
      "Basic SEO setup",
    ],
    bestFor: "Personal brands, MVPs, side hustles",
    popular: false,
    cta: "Book a call",
  },
  {
    name: "Professional",
    price: "3,500",
    description: "More pages, more polish, more power.",
    includesFrom: "Everything in Starter, plus:",
    highlights: [
      "Custom website (5-10 pages)",
      "Advanced animations & interactions",
      "Analytics & performance tracking",
      "SEO optimisation",
      "API integrations (payments, bookings, CRM)",
    ],
    bestFor: "Startups, growing businesses",
    popular: true,
    cta: "Book a call",
  },
  {
    name: "Enterprise",
    price: "10,000",
    description: "Built for complexity. Managed with care.",
    includesFrom: "Everything in Professional, plus:",
    highlights: [
      "Fully bespoke website",
      "Complex integrations (CRM/ERP)",
      "Booking systems & dashboards",
      "Logo creation & branding",
      "Dedicated strategy & support",
    ],
    bestFor: "Scale-ups, established brands",
    popular: false,
    cta: "Book a call",
  },
];

const carePlans = [
  {
    name: "Keep it Live",
    monthlyPrice: 30,
    annualPrice: 300, // 10 months
    description: "Hosting, security, and peace of mind. We keep the lights on.",
    featured: true,
    features: [
      "Hosting & SSL included",
      "Domain & email management",
      "Uptime monitoring",
      "Email support",
    ],
  },
  {
    name: "Keep it Fresh",
    monthlyPrice: 60,
    annualPrice: 600, // 10 months
    description: "Regular updates and maintenance so your site stays sharp.",
    features: [
      "Everything in Keep it Live",
      "Software & security patches",
      "Performance monitoring",
      "Monthly health report",
      "Faster response times",
    ],
  },
  {
    name: "Keep it Growing",
    monthlyPrice: 115,
    annualPrice: 1150, // 10 months
    description: "Ongoing content, SEO, and strategy to drive results.",
    features: [
      "Everything in Keep it Fresh",
      "Monthly content updates",
      "SEO optimisation & reporting",
      "Priority support",
      "Quarterly strategy call",
    ],
  },
];

// `domain` drives the address bar; keep it exactly as it appears in a real URL bar.
const portfolioSites = [
  {
    name: "Convu.ai",
    domain: "convu.ai",
    url: "https://www.convu.ai",
    sector: "AI / SaaS",
    image: "/assets/images/website-examples/convu-landing.webp",
    description: "Turning networking into revenue with AI-powered follow-ups",
  },
  {
    name: "Jalaram Beauty",
    domain: "jalarambeauty.com",
    url: "https://www.jalarambeauty.com",
    sector: "Beauty salon",
    image: "/assets/images/website-examples/jalarambeauty-landing.webp",
    description: "Seamless booking for a thriving beauty salon",
  },
  {
    name: "Quantum Tuition",
    domain: "quantumtuition.co.uk",
    url: "https://www.quantumtuition.co.uk",
    sector: "Education",
    image: "/assets/images/website-examples/quantumtutoring-landing.webp",
    description: "Making learning easy with expert private tutoring",
  },
  {
    name: "Foodu.ai",
    domain: "foodu.ai",
    url: "https://www.foodu.ai",
    sector: "Hospitality",
    image: "/assets/images/website-examples/foodu-landing.webp",
    description: "Turning restaurant guests into loyal repeat customers",
  },
  {
    name: "eDharma",
    domain: "edharma.co.uk",
    url: "https://edharma.co.uk",
    sector: "Student services",
    image: "/assets/images/website-examples/edharma-landing.webp",
    description: "Helping students navigate their university journey",
  },
];

const included = [
  { icon: Code2, label: "Custom website", detail: "Built from scratch" },
  { icon: Mail, label: "Professional email", detail: "you@yourbusiness.com" },
  { icon: Calendar, label: "Calendar setup", detail: "Bookings, integrated" },
  { icon: Sparkles, label: "Motion & polish", detail: "Smooth and modern" },
  { icon: ShieldCheck, label: "Domain & hosting", detail: "Secured with SSL" },
];

const processSteps = [
  {
    icon: MessageCircle,
    step: "01",
    title: "Tell us what you need",
    description: "A quick call. We learn about your business, you tell us what you want.",
    yours: true,
  },
  {
    icon: PenTool,
    step: "02",
    title: "We design it",
    description: "Custom designs built around your brand. You review, we refine.",
    yours: true,
  },
  {
    icon: Code2,
    step: "03",
    title: "We build it",
    description: "We develop, test, and set up everything - site, email, domain, the lot.",
    yours: false,
  },
  {
    icon: Rocket,
    step: "04",
    title: "You're live",
    description: "Everything is ready. You're online. We're still here if you need us.",
    yours: false,
  },
];

const testimonials = [
  {
    quote: "DataPulse Web built our site in 4 days. Fast, clean, no hassle and professional.",
    name: "Founder",
    role: "Convu.ai",
    url: "https://www.convu.ai",
  },
  {
    quote: "I just told them what I needed and they handled absolutely everything. Domain, email, the lot.",
    name: "Jalaram Beauty",
    role: "Salon Owner",
    url: "https://www.jalarambeauty.com",
  },
  {
    quote: "We went from no online presence to a fully professional setup in under two weeks.",
    name: "Quantum Tuition",
    role: "Education",
    url: "https://www.quantumtuition.co.uk",
  },
];

const faqs = [
  {
    q: "What exactly is included in the package?",
    a: "Everything you need to be online. We build your custom website, register your domain, set up hosting, create your professional email address (e.g. you@yourbusiness.com), and integrate your calendar for bookings. You don't need to set up anything yourself.",
  },
  {
    q: "Do I need any technical knowledge?",
    a: "None at all. We handle all the technical setup. You just tell us what your business does and what you want - we do the rest.",
  },
  {
    q: "How long does it take?",
    a: "We aim to deliver your site within 2 weeks. Most sites are live even sooner. We'll keep you updated throughout.",
  },
  {
    q: "What happens after my site goes live?",
    a: "We don't disappear. Choose one of our care plans (from just £30/mo) and we'll keep your site updated, secure, and running smoothly. Or manage it yourself - it's your site.",
  },
  {
    q: "Can I see examples of your work?",
    a: "Absolutely. Scroll up to the Our Work section to see live sites we've built for real businesses, or book a call and we'll walk you through them.",
  },
  {
    q: "What if I need changes after launch?",
    a: "Small tweaks in the first 2 weeks after launch are on us. After that, our care plans include ongoing updates, or you can request changes on a per-project basis.",
  },
];

const trustPoints = [
  "Live in under 2 weeks",
  "100% managed for you",
  "Fixed quotes, no hidden fees",
  "UK based, real humans",
];

const navLinks = [
  { href: "#work", label: "Work" },
  { href: "#process", label: "Process" },
  { href: "#packages", label: "Packages" },
  { href: "#faq", label: "FAQ" },
];

/* ------------------------------------------------------------------ *
 * Small building blocks
 * ------------------------------------------------------------------ */

/**
 * The page's one structural device: an orange hairline, a mono route label,
 * then the heading. Routes are used because sections of a website *are* routes,
 * which is meaningful for a company that builds websites.
 */
function SectionHead({ route, title, blurb, center = false }) {
  return (
    <div className={center ? "mx-auto max-w-2xl text-center" : "max-w-2xl"}>
      <div className={`mb-5 flex items-center gap-3 ${center ? "justify-center" : ""}`}>
        <span className="h-px w-6 bg-[#FF6A3D]" aria-hidden="true" />
        <span className="dp-mono text-[11px] uppercase tracking-[0.22em] text-[#FF6A3D]">{route}</span>
      </div>
      <h2 className="dp-display text-[clamp(1.75rem,4.4vw,2.9rem)] font-semibold leading-[1.06] tracking-[-0.025em] text-[#EDF1F7]">
        <MaskReveal>{title}</MaskReveal>
      </h2>
      {blurb && (
        <p className="mt-4 text-[15px] leading-relaxed text-[#7E93B2] sm:text-base">{blurb}</p>
      )}
    </div>
  );
}

function FaqItem({ faq, isOpen, onToggle, id }) {
  const reduce = useReducedMotion();

  return (
    <div
      className={`overflow-hidden rounded-xl border transition-colors duration-300 ${
        isOpen ? "border-[#2A4066] bg-[#0B1423]" : "border-[#1A2A44] bg-[#080F1C] hover:border-[#25395A]"
      }`}
    >
      <h3>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={`faq-panel-${id}`}
          id={`faq-trigger-${id}`}
          className="dp-focus flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-[15px] font-medium text-[#EDF1F7]"
        >
          {faq.q}
          <ChevronDown
            className={`h-4 w-4 shrink-0 transition-transform duration-300 ${
              isOpen ? "rotate-180 text-[#FF6A3D]" : "text-[#5C749B]"
            }`}
            aria-hidden="true"
          />
        </button>
      </h3>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={`faq-panel-${id}`}
            role="region"
            aria-labelledby={`faq-trigger-${id}`}
            initial={reduce ? false : { height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={reduce ? undefined : { height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="px-5 pb-5 text-[14.5px] leading-relaxed text-[#8FA3C0]">{faq.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Hero — the signature moment
 * ------------------------------------------------------------------ */

function HeroShowcase() {
  const frameRef = useRef(null);
  const inView = useInView(frameRef, { amount: 0.35 });
  const [hovered, setHovered] = useState(false);
  const isDesktop = useIsDesktop();

  // The address bar cycles on desktop only. On a phone it was the single most
  // expensive thing on the page: a React state update per typed character (every
  // 13-38ms, forever) plus an eager download of all five screenshots within the
  // first few seconds, all competing with the first paint. Mobile gets the first
  // site, held still — the frame still tells the story without the churn.
  const cycling = isDesktop && inView && !hovered;
  const { index, typed, phase, caret } = useAddressBar(portfolioSites, cycling, isDesktop);
  const site = portfolioSites[index];

  // Warm the next screenshot so the load sweep lands on a decoded image. Only
  // worth doing while something is actually cycling.
  useEffect(() => {
    if (!cycling) return;
    const next = portfolioSites[(index + 1) % portfolioSites.length];
    const img = new Image();
    img.src = next.image;
  }, [index, cycling]);

  return (
    <div ref={frameRef} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div className="relative">
        {/* Depth behind the frame — the only glow on the page */}
        <div
          className="pointer-events-none absolute -inset-8 -z-10 rounded-[2rem] opacity-70 blur-3xl"
          style={{
            background:
              "radial-gradient(60% 60% at 30% 20%, rgba(56,189,248,0.16), transparent 70%), radial-gradient(50% 50% at 80% 80%, rgba(255,106,61,0.14), transparent 70%)",
          }}
          aria-hidden="true"
        />

        <BrowserFrame url={`https://${typed}`} caret={caret} phase={phase}>
          {/* Images stack absolutely so they crossfade instead of flashing the empty frame */}
          <div className="relative aspect-[16/10] overflow-hidden">
            <AnimatePresence>
              <SiteShot
                key={site.image}
                image={site.image}
                alt={`${site.name} website, built by DataPulse Web`}
                priority
                className="absolute inset-0 h-full w-full object-cover object-top"
                initial={{ opacity: 0, scale: 1.015 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: EASE }}
              />
            </AnimatePresence>
          </div>
        </BrowserFrame>
      </div>

      {/* Caption ties the demo to a real business */}
      <div className="mt-4 flex items-center justify-between gap-4 px-1">
        <motion.div
          key={site.name}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: EASE }}
          className="min-w-0"
        >
          <p className="truncate text-sm font-medium text-[#EDF1F7]">{site.name}</p>
          <p className="dp-mono text-[11px] uppercase tracking-[0.14em] text-[#5C749B]">
            {site.sector}
          </p>
        </motion.div>
        <a
          href={site.url}
          target="_blank"
          rel="noopener noreferrer"
          className="dp-focus group inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#1A2A44] px-3.5 py-1.5 text-[13px] text-[#B6C6DE] transition-colors duration-300 hover:border-[#FF6A3D]/50 hover:text-white"
        >
          Visit site
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */

function Websites() {
  const reduce = useReducedMotion();
  const isDesktop = useIsDesktop();
  const [isAnnual, setIsAnnual] = useState(true);
  const [openFaq, setOpenFaq] = useState(0);
  const [pastHero, setPastHero] = useState(false);

  // The sticky mobile CTA nudges you toward the form, then retires for good once you
  // arrive there, so it never covers the form or the footer.
  const startRef = useRef(null);
  const startInView = useInView(startRef, { amount: 0.1 });
  const [startReached, setStartReached] = useState(false);
  useEffect(() => {
    if (startInView) setStartReached(true);
  }, [startInView]);
  const showStickyCta = pastHero && !startReached;

  useEffect(() => {
    const onScroll = () => setPastHero(window.scrollY > 700);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Ambient particles: randomised rather than a fixed symmetrical set, kept very low
  // contrast. Each one is a permanently-animating composited layer, so a phone gets a
  // third of the count. At this opacity the difference is invisible, but twenty of
  // them on a mobile GPU is a measurable chunk of the jank.
  const particleCount = isDesktop ? 20 : 7;
  const particles = useMemo(
    () =>
      Array.from({ length: particleCount }, (_, i) => ({
        id: i,
        left: 4 + Math.random() * 92,
        top: 6 + Math.random() * 84,
        size: 2 + Math.random() * 3.5,
        duration: 14 + Math.random() * 12,
        delay: Math.random() * 10,
        warm: Math.random() > 0.72,
      })),
    [particleCount]
  );

  // Entrance timing for the hero. Collapses to no animation when motion is reduced.
  const rise = (delay = 0) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 16 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.8, ease: EASE, delay },
        };

  return (
    <>
      <Helmet>
        <title>UK Website Design, Fully Managed | DataPulse Web</title>
        <meta
          name="description"
          content="We design, build and manage your website - domain, hosting, professional email and bookings all set up for you. Live in under two weeks. Book a free call."
        />
        <link rel="canonical" href="https://www.mydatapulse.co.uk/" />
        <meta name="robots" content="index, follow" />

        {/* Typography (Bricolage Grotesque, Instrument Sans, IBM Plex Mono) is
            declared in public/index.html so it starts downloading before React
            mounts. Repeating the stylesheet link here only re-requested it after
            hydration, which is strictly later and strictly slower. */}

        <meta property="og:site_name" content="DataPulse Web" />
        <meta property="og:title" content="UK Website Design, Fully Managed | DataPulse Web" />
        <meta
          property="og:description"
          content="We design, build and manage your website - domain, hosting, professional email and bookings all set up for you. Live in under two weeks."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.mydatapulse.co.uk/" />
        <meta property="og:image" content="https://www.mydatapulse.co.uk/website-og.jpg?v=1" />
        <meta property="og:image:alt" content="DataPulse Web - we handle your website, you handle your business" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@datapulseai" />
        <meta name="twitter:title" content="UK Website Design, Fully Managed | DataPulse Web" />
        <meta
          name="twitter:description"
          content="We design, build and manage your website - domain, hosting, professional email and bookings all set up for you. Live in under two weeks."
        />
        <meta name="twitter:image" content="https://www.mydatapulse.co.uk/website-og.jpg?v=1" />
        <meta name="twitter:image:alt" content="DataPulse Web - we handle your website, you handle your business" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Fully Managed Website Design",
            serviceType: "Website design, build, hosting and management",
            provider: {
              "@type": "Organization",
              name: "DataPulse Web",
              url: "https://www.mydatapulse.co.uk",
              // No street address is claimed, so this stays an Organization rather
              // than a LocalBusiness (which would be invalid without one).
              contactPoint: {
                "@type": "ContactPoint",
                contactType: "sales",
                telephone: "+44-7429-450382",
                email: "info@mydatapulse.co.uk",
                areaServed: "GB",
                availableLanguage: "English",
              },
            },
            description:
              "We design, build and manage websites for UK businesses, including domain registration, hosting, professional email and booking calendars.",
            areaServed: { "@type": "Country", name: "United Kingdom" },
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: "Website Packages",
              itemListElement: [
                {
                  "@type": "Offer",
                  itemOffered: { "@type": "Service", name: "Starter Website" },
                  priceSpecification: {
                    "@type": "PriceSpecification",
                    price: "1500",
                    priceCurrency: "GBP",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: { "@type": "Service", name: "Professional Website" },
                  priceSpecification: {
                    "@type": "PriceSpecification",
                    price: "3500",
                    priceCurrency: "GBP",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: { "@type": "Service", name: "Enterprise Website" },
                  priceSpecification: {
                    "@type": "PriceSpecification",
                    price: "10000",
                    priceCurrency: "GBP",
                  },
                },
              ],
            },
          })}
        </script>

        {/* FAQ answers live behind an accordion, so expose them to search here too. */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
      </Helmet>

      <WebStyles />

      <div className="dp-body bg-[#060B14] text-[#EDF1F7] antialiased">
        {/* Same-page anchors here; WebNav's defaults are the /#... form for subpages. */}
        <WebNav links={navLinks} ctaHref="/appointment" ctaLabel="Book a call" />
        <main>
          {/* ---------------------------------------------------------- Hero */}
          <section className="relative flex min-h-[100svh] flex-col justify-center overflow-hidden px-5 pb-14 pt-28 sm:px-8 sm:pb-16 sm:pt-32">
            {/* Ambient layer */}
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
              <div className="dp-grid absolute inset-0" />
              <div className="absolute -top-24 left-[8%] h-[26rem] w-[26rem] rounded-full bg-[#38BDF8]/[0.07] blur-[130px]" />
              <div className="absolute bottom-0 right-[4%] h-[22rem] w-[22rem] rounded-full bg-[#FF6A3D]/[0.06] blur-[120px]" />
              {particles.map((p) => (
                <span
                  key={p.id}
                  className="dp-particle"
                  style={{
                    left: `${p.left}%`,
                    top: `${p.top}%`,
                    width: p.size,
                    height: p.size,
                    background: p.warm
                      ? "radial-gradient(circle, rgba(255,106,61,0.55) 0%, transparent 70%)"
                      : "radial-gradient(circle, rgba(56,189,248,0.5) 0%, transparent 70%)",
                    animation: `dp-drift ${p.duration}s ease-in-out ${p.delay}s infinite`,
                  }}
                />
              ))}
            </div>

            <div className="relative mx-auto max-w-6xl">
              <div className="grid items-center gap-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:gap-16">
              {/* Copy */}
              <div>
                <motion.div {...rise(0)} className="mb-7 flex items-center gap-3">
                  <span className="h-px w-6 bg-[#FF6A3D]" aria-hidden="true" />
                  <span className="dp-mono text-[11px] uppercase tracking-[0.22em] text-[#7E93B2]">
                    Fully managed websites · UK
                  </span>
                </motion.div>

                <motion.h1
                  {...rise(0.08)}
                  className="dp-display text-[clamp(2.35rem,6.4vw,4rem)] font-semibold leading-[0.99] tracking-[-0.035em] text-white"
                >
                  We handle your website.{" "}
                  <span className="text-[#B6C6DE]">
                    You handle{" "}
                    <span className="relative inline-block whitespace-nowrap">
                      your business.
                      <motion.span
                        className="absolute -bottom-0.5 left-0 h-[3px] w-full origin-left rounded-full bg-[#FF6A3D]"
                        initial={reduce ? false : { scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.85 }}
                        aria-hidden="true"
                      />
                    </span>
                  </span>
                </motion.h1>

                <motion.p
                  {...rise(0.16)}
                  className="mt-7 max-w-xl text-[16.5px] leading-relaxed text-[#8FA3C0] sm:text-[17.5px]"
                >
                  Tell us what you need. We build the site, register the domain, set up your
                  professional email and calendar, and put you live in under two weeks.
                </motion.p>

                <motion.p {...rise(0.22)} className="mt-3 text-[14px] text-[#5C749B]">
                  No technical knowledge needed. No hidden fees.
                </motion.p>

                <motion.div {...rise(0.3)} className="mt-9 flex flex-col gap-3 sm:flex-row">
                  <a
                    href="/appointment"
                    className="dp-focus group inline-flex items-center justify-center gap-2 rounded-full bg-[#FF6A3D] px-7 py-3.5 text-[15px] font-semibold text-white shadow-[0_12px_36px_-12px_rgba(255,106,61,0.6)] transition-colors duration-300 hover:bg-[#FF8055]"
                  >
                    Book a call
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </a>
                  <a
                    href="#packages"
                    className="dp-focus inline-flex items-center justify-center rounded-full border border-[#25395A] px-7 py-3.5 text-[15px] font-medium text-[#EDF1F7] transition-colors duration-300 hover:border-[#3D5680] hover:bg-[#0B1423]"
                  >
                    See packages
                  </a>
                </motion.div>

                <motion.p {...rise(0.36)} className="mt-4 text-[13px] text-[#4C6A94]">
                  15 minutes, no commitment. You'll get a plan and a fixed quote.
                </motion.p>
              </div>

                {/* Signature: the address bar */}
                <motion.div
                  initial={reduce ? false : { opacity: 0, y: 26, scale: 0.975 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 1, ease: EASE, delay: 0.24 }}
                >
                  <HeroShowcase />
                </motion.div>
              </div>

              {/* Trust strip spans both columns, so on mobile the live demo comes
                  before it rather than after a long list. */}
              <motion.ul
                {...rise(0.44)}
                className="mt-14 flex flex-wrap gap-x-8 gap-y-3 border-t border-[#121E33] pt-6"
              >
                {trustPoints.map((point) => (
                  <li
                    key={point}
                    className="dp-mono flex items-center gap-2 text-[11px] uppercase tracking-[0.12em] text-[#7E93B2]"
                  >
                    <Check className="h-3 w-3 shrink-0 text-[#38BDF8]" aria-hidden="true" />
                    {point}
                  </li>
                ))}
              </motion.ul>
            </div>

            {/* Scroll cue, anchored to the bottom of the full-height hero */}
            <motion.div
              className="pointer-events-none absolute inset-x-0 bottom-5 hidden justify-center lg:flex"
              initial={reduce ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, ease: EASE, delay: 1.3 }}
              aria-hidden="true"
            >
              <motion.span
                className="dp-mono flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#33486B]"
                animate={reduce ? undefined : { y: [0, 5, 0] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
              >
                <ArrowDown className="h-3 w-3" />
                See the work
              </motion.span>
            </motion.div>
          </section>

          {/* Live client domains, always in motion */}
          <DomainMarquee sites={portfolioSites} />

          {/* ------------------------------------------------- What's included */}
          <section className="px-5 py-16 sm:px-8 sm:py-20">
            <div className="mx-auto max-w-6xl">
              <Reveal>
                <SectionHead
                  route="/included"
                  title="Every package ships with all of it."
                  blurb="No add-on list, no upsell path. This is the baseline on the cheapest package we sell."
                />
              </Reveal>

              <Reveal delay={0.08}>
                {/* 1px grid gap over a hairline-coloured background draws the dividers,
                    so the cells divide correctly at every breakpoint with no per-cell logic. */}
                <div className="mt-10 grid grid-cols-2 gap-px overflow-hidden rounded-2xl bg-[#16233A] ring-1 ring-[#16233A] md:grid-cols-5">
                  {included.map((item, i) => (
                    <div
                      key={item.label}
                      className={`group bg-[#080F1C] p-5 transition-colors duration-300 hover:bg-[#0B1423] sm:p-6 ${
                        i === included.length - 1 ? "col-span-2 md:col-span-1" : ""
                      }`}
                    >
                      <item.icon
                        className="h-5 w-5 text-[#38BDF8] transition-transform duration-500 group-hover:-translate-y-0.5"
                        aria-hidden="true"
                      />
                      <p className="mt-4 text-[14.5px] font-medium text-[#EDF1F7]">{item.label}</p>
                      <p className="dp-mono mt-1.5 text-[11px] tracking-tight text-[#5C749B]">
                        {item.detail}
                      </p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </section>

          {/* ------------------------------------------------------------ Work */}
          <section id="work" className="scroll-mt-0">
            <WorkShowcase
              sites={portfolioSites}
              heading={
                <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                  <SectionHead route="/work" title="Real sites. Real businesses." />
                  <p className="max-w-xs text-[14.5px] leading-relaxed text-[#7E93B2] md:text-right">
                    Designed, built, hosted and launched by us. Open them and judge for yourself.
                  </p>
                </div>
              }
            />
          </section>

          {/* --------------------------------------------------------- Process */}
          <section id="process" className="scroll-mt-0">
            <BuildSequence
              site={portfolioSites[0]}
              steps={processSteps}
              heading={
                <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                  <SectionHead route="/process" title="Watch one get built." />
                  <p className="max-w-xs text-[14.5px] leading-relaxed text-[#7E93B2] md:text-right">
                    Four steps, and you're only in two of them. Keep scrolling.
                  </p>
                </div>
              }
            />
          </section>

          {/* -------------------------------------------------------- Packages */}
          <section id="packages" className="scroll-mt-24 px-5 py-16 sm:px-8 sm:py-24">
            <div className="mx-auto max-w-6xl">
              <Reveal>
                <SectionHead
                  route="/packages"
                  title="One package. Everything handled."
                  blurb="Website, domain, hosting, email, scheduling - all set up and managed for you. We aim to deliver your site within 2 weeks."
                  center
                />
              </Reveal>

              <div className="mt-12 grid items-stretch gap-5 md:grid-cols-3">
                {pricingTiers.map((tier, i) => (
                  <Reveal key={tier.name} delay={i * 0.08} className="h-full">
                    <div
                      className={`relative flex h-full flex-col rounded-2xl border p-6 transition-colors duration-300 sm:p-7 ${
                        tier.popular
                          ? "border-[#2F4770] bg-gradient-to-b from-[#0E1B2E] to-[#0A1220] shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)]"
                          : "border-[#16233A] bg-[#080F1C] hover:border-[#223452]"
                      }`}
                    >
                      {tier.popular && (
                        <>
                          <span
                            className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#FF6A3D] to-transparent"
                            aria-hidden="true"
                          />
                          <span className="dp-mono absolute -top-2.5 left-6 rounded-full bg-[#FF6A3D] px-2.5 py-1 text-[9.5px] font-medium uppercase tracking-[0.14em] text-white">
                            Most popular
                          </span>
                        </>
                      )}

                      <h3 className="dp-display text-xl font-semibold tracking-[-0.02em] text-white">
                        {tier.name}
                      </h3>
                      <p className="mt-2 min-h-[2.75rem] text-[14px] leading-relaxed text-[#7E93B2]">
                        {tier.description}
                      </p>

                      <div className="mt-6 flex items-baseline gap-2">
                        <span className="dp-mono text-[10px] uppercase tracking-[0.18em] text-[#4C6A94]">
                          from
                        </span>
                        <span className="dp-mono text-[2.1rem] font-medium leading-none tracking-[-0.03em] text-white">
                          £{tier.price}
                        </span>
                      </div>

                      <div className="mt-7 flex-grow border-t border-[#16233A] pt-6">
                        {tier.includesFrom && (
                          <p className="dp-mono mb-4 text-[10.5px] uppercase tracking-[0.14em] text-[#4C6A94]">
                            {tier.includesFrom}
                          </p>
                        )}
                        <ul className="space-y-3">
                          {tier.highlights.map((highlight) => (
                            <li
                              key={highlight}
                              className="flex items-start gap-2.5 text-[14px] leading-snug text-[#D3DEED]"
                            >
                              <Check
                                className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${
                                  tier.popular ? "text-[#FF6A3D]" : "text-[#38BDF8]"
                                }`}
                                aria-hidden="true"
                              />
                              <span>{highlight}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="mt-7 border-t border-[#16233A] pt-5">
                        <p className="mb-4 text-[12.5px] leading-snug text-[#5C749B]">
                          <span className="dp-mono uppercase tracking-[0.12em]">Best for</span>
                          <br />
                          {tier.bestFor}
                        </p>
                        <a
                          href={`/appointment?package=${tier.name}`}
                          className={`dp-focus group flex w-full items-center justify-center gap-2 rounded-full py-3 text-[14px] font-semibold transition-colors duration-300 ${
                            tier.popular
                              ? "bg-[#FF6A3D] text-white hover:bg-[#FF8055]"
                              : "border border-[#25395A] text-[#EDF1F7] hover:border-[#3D5680] hover:bg-[#0E1929]"
                          }`}
                        >
                          {tier.cta}
                          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                        </a>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>

              <p className="dp-mono mt-6 text-center text-[10.5px] uppercase tracking-[0.16em] text-[#3A4E6E]">
                All prices exclude VAT
              </p>
            </div>
          </section>

          {/* ------------------------------------------------------ Care plans */}
          <section className="px-5 py-16 sm:px-8 sm:py-24">
            <div className="mx-auto max-w-5xl">
              <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
                <Reveal>
                  <SectionHead
                    route="/care"
                    title="After launch, we're still here."
                    blurb="Pick a care plan and we keep your site live, current and growing. Or take the keys and run it yourself - it's your site."
                  />
                </Reveal>

                <Reveal delay={0.08}>
                  <div
                    className="inline-flex shrink-0 items-center rounded-full border border-[#16233A] bg-[#080F1C] p-1"
                    role="group"
                    aria-label="Billing period"
                  >
                    <button
                      type="button"
                      onClick={() => setIsAnnual(false)}
                      aria-pressed={!isAnnual}
                      className={`dp-focus relative rounded-full px-5 py-2 text-[13px] font-medium transition-colors duration-300 ${
                        !isAnnual ? "text-[#060B14]" : "text-[#7E93B2] hover:text-white"
                      }`}
                    >
                      {!isAnnual && (
                        <motion.span
                          layoutId="billing-pill"
                          className="absolute inset-0 rounded-full bg-[#EDF1F7]"
                          transition={{ duration: 0.3, ease: EASE }}
                        />
                      )}
                      <span className="relative">Monthly</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsAnnual(true)}
                      aria-pressed={isAnnual}
                      className={`dp-focus relative rounded-full px-5 py-2 text-[13px] font-medium transition-colors duration-300 ${
                        isAnnual ? "text-[#060B14]" : "text-[#7E93B2] hover:text-white"
                      }`}
                    >
                      {isAnnual && (
                        <motion.span
                          layoutId="billing-pill"
                          className="absolute inset-0 rounded-full bg-[#EDF1F7]"
                          transition={{ duration: 0.3, ease: EASE }}
                        />
                      )}
                      <span className="relative">
                        Annual{" "}
                        <span className={isAnnual ? "text-[#C2451D]" : "text-[#FF6A3D]"}>-17%</span>
                      </span>
                    </button>
                  </div>
                </Reveal>
              </div>

              <div className="mt-12 grid gap-5 md:grid-cols-3">
                {carePlans.map((plan, i) => (
                  <Reveal key={plan.name} delay={i * 0.08} className="h-full">
                    <div
                      className={`relative flex h-full flex-col rounded-2xl border p-6 transition-colors duration-300 ${
                        plan.featured
                          ? "border-[#2F4770] bg-gradient-to-b from-[#0E1B2E] to-[#0A1220]"
                          : "border-[#16233A] bg-[#080F1C]"
                      }`}
                    >
                      {plan.featured && (
                        <>
                          <span
                            className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#FF6A3D] to-transparent"
                            aria-hidden="true"
                          />
                          <span className="dp-mono absolute -top-2.5 left-6 rounded-full bg-[#FF6A3D] px-2.5 py-1 text-[9.5px] font-medium uppercase tracking-[0.14em] text-white">
                            Recommended
                          </span>
                        </>
                      )}
                      <h3 className="dp-display text-[17px] font-semibold tracking-[-0.015em] text-white">
                        {plan.name}
                      </h3>
                      <p className="mt-2 min-h-[2.75rem] text-[13.5px] leading-relaxed text-[#7E93B2]">
                        {plan.description}
                      </p>

                      <div className="mt-5 flex items-baseline gap-1.5">
                        <span className="dp-mono text-[1.85rem] font-medium leading-none tracking-[-0.03em] text-white">
                          £{isAnnual ? Math.round(plan.annualPrice / 12) : plan.monthlyPrice}
                        </span>
                        <span className="dp-mono text-[11px] text-[#5C749B]">/mo</span>
                      </div>
                      <p className="dp-mono mt-2 h-4 text-[10.5px] uppercase tracking-[0.12em] text-[#4C6A94]">
                        {isAnnual ? `Billed £${plan.annualPrice.toLocaleString()}/year` : ""}
                      </p>

                      <ul className="mt-6 space-y-2.5 border-t border-[#16233A] pt-5">
                        {plan.features.map((feature) => (
                          <li
                            key={feature}
                            className="flex items-start gap-2.5 text-[13.5px] leading-snug text-[#D3DEED]"
                          >
                            <Check
                              className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${
                                plan.featured ? "text-[#FF6A3D]" : "text-[#38BDF8]"
                              }`}
                              aria-hidden="true"
                            />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* --------------------------------------------------------- Reviews */}
          <section className="px-5 py-16 sm:px-8 sm:py-24">
            <div className="mx-auto max-w-6xl">
              <Reveal>
                <SectionHead
                  route="/reviews"
                  title="Don't take our word for it."
                  blurb="Three clients, three sectors, three sites you can open right now."
                />
              </Reveal>

              <div className="mt-12 grid gap-5 md:grid-cols-3">
                {testimonials.map((t, i) => (
                  <Reveal key={t.role} delay={i * 0.08} className="h-full">
                    <figure className="flex h-full flex-col rounded-2xl border border-[#16233A] bg-[#080F1C] p-6 transition-colors duration-300 hover:border-[#223452]">
                      <div className="flex gap-0.5" aria-label="Five out of five">
                        {[...Array(5)].map((_, s) => (
                          <Star key={s} className="h-3.5 w-3.5 fill-[#FF6A3D] text-[#FF6A3D]" aria-hidden="true" />
                        ))}
                      </div>
                      <blockquote className="dp-display mt-5 flex-grow text-[17px] font-medium leading-snug tracking-[-0.01em] text-[#EDF1F7]">
                        {t.quote}
                      </blockquote>
                      <figcaption className="mt-6 flex items-end justify-between gap-3 border-t border-[#16233A] pt-5">
                        <div>
                          <p className="text-[13.5px] font-medium text-[#EDF1F7]">{t.name}</p>
                          <p className="dp-mono text-[10.5px] uppercase tracking-[0.14em] text-[#5C749B]">
                            {t.role}
                          </p>
                        </div>
                        <a
                          href={t.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="dp-focus group inline-flex items-center gap-1 text-[12.5px] text-[#FF6A3D] transition-colors duration-300 hover:text-[#FF8B63]"
                        >
                          Visit
                          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                      </figcaption>
                    </figure>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* ------------------------------------------------------------- FAQ */}
          <section id="faq" className="scroll-mt-24 px-5 py-16 sm:px-8 sm:py-24">
            <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[22rem_minmax(0,1fr)] lg:gap-16">
              <Reveal>
                <div className="lg:sticky lg:top-28">
                  <SectionHead
                    route="/faq"
                    title="Common questions."
                    blurb="Everything worth knowing before you get started."
                  />
                  <a
                    href="/appointment"
                    className="dp-focus group mt-7 inline-flex items-center gap-2 text-sm font-medium text-[#FF6A3D] transition-colors duration-300 hover:text-[#FF8B63]"
                  >
                    Still unsure? Ask us directly
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </a>
                </div>
              </Reveal>

              <div className="space-y-2.5">
                {faqs.map((faq, i) => (
                  <Reveal key={faq.q} delay={i * 0.05}>
                    <FaqItem
                      faq={faq}
                      id={i}
                      isOpen={openFaq === i}
                      onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
                    />
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* ----------------------------------------------------------- Start */}
          <section id="start" ref={startRef} className="scroll-mt-24 px-5 py-16 sm:px-8 sm:py-24">
            <div className="mx-auto max-w-5xl">
              <Reveal>
                <SectionHead
                  route="/start"
                  title="Let's get you online."
                  blurb="We take on a limited number of projects each month to keep quality high. Pick whichever works for you and we'll take it from there."
                  center
                />
              </Reveal>

              <div className="mt-12 grid gap-4 sm:grid-cols-3">
                {[
                  {
                    href: "/appointment",
                    icon: Calendar,
                    title: "Book a free call",
                    sub: "No obligation, no sales pitch",
                    value: "15 min, just a chat",
                    external: false,
                  },
                  {
                    href: "https://wa.me/447429450382?text=Hi%20DataPulse%20Web.%20I'd%20love%20to%20learn%20more%20about%20your%20website%20services!",
                    icon: MessageCircle,
                    title: "WhatsApp",
                    sub: "Chat with us instantly",
                    value: "+44 7429 450382",
                    external: true,
                  },
                  {
                    href: "mailto:info@mydatapulse.co.uk?subject=Let's%20Build%20Something%20Great",
                    icon: Mail,
                    title: "Email",
                    sub: "Reach out any time",
                    value: "info@mydatapulse.co.uk",
                    external: false,
                  },
                ].map((card, i) => (
                  <Reveal key={card.title} delay={i * 0.08} className="h-full">
                    <a
                      href={card.href}
                      {...(card.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      className="dp-focus group flex h-full flex-col rounded-2xl border border-[#16233A] bg-[#080F1C] p-6 transition-colors duration-300 hover:border-[#2F4770] hover:bg-[#0B1423]"
                    >
                      <card.icon
                        className="h-5 w-5 text-[#38BDF8] transition-transform duration-500 group-hover:-translate-y-0.5"
                        aria-hidden="true"
                      />
                      <p className="mt-5 text-[15px] font-medium text-[#EDF1F7]">{card.title}</p>
                      <p className="mt-1 text-[13px] text-[#5C749B]">{card.sub}</p>
                      <p className="dp-mono mt-auto pt-5 text-[11.5px] tracking-tight text-[#B6C6DE]">
                        {card.value}
                      </p>
                    </a>
                  </Reveal>
                ))}
              </div>

              {/* Enquiry form */}
              <Reveal delay={0.16}>
                <div className="mt-5 overflow-hidden rounded-2xl border border-[#16233A] bg-[#080F1C]">
                  <div className="grid lg:grid-cols-[1fr_1.15fr]">
                    <div className="border-b border-[#16233A] bg-[#0B1423] p-7 sm:p-9 lg:border-b-0 lg:border-r">
                      <h3 className="dp-display text-2xl font-semibold tracking-[-0.025em] text-white">
                        Tell us what you need.
                      </h3>
                      <p className="mt-3 text-[15px] leading-relaxed text-[#8FA3C0]">
                        Drop us a message and we'll come back with a clear plan and a fixed quote. No
                        commitment, no jargon, no surprises.
                      </p>
                      <ul className="mt-8 space-y-3 border-t border-[#16233A] pt-6">
                        {[
                          "We reply within one working day",
                          "You get a fixed quote, not an estimate",
                          "Nothing to pay until you say yes",
                        ].map((line) => (
                          <li key={line} className="flex items-start gap-2.5 text-[13.5px] text-[#B6C6DE]">
                            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#38BDF8]" aria-hidden="true" />
                            {line}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="p-7 sm:p-9">
                      <HubSpotForm
                        portalId="146939653"
                        formId="1d0e8af4-6332-4161-b614-cec6fabc5282"
                        fields={[
                          {
                            name: "firstname",
                            label: "First name",
                            type: "text",
                            placeholder: "Your first name",
                            required: true,
                            className: "w-full rounded-lg border border-[#1A2A44] bg-[#060B14] px-4 py-3 text-[15px] text-white placeholder-[#3A4E6E] transition-colors duration-200 focus:border-[#FF6A3D] focus:outline-none focus:ring-2 focus:ring-[#FF6A3D]/25",
                          },
                          {
                            name: "lastname",
                            label: "Last name",
                            type: "text",
                            placeholder: "Your last name",
                            required: true,
                            className: "w-full rounded-lg border border-[#1A2A44] bg-[#060B14] px-4 py-3 text-[15px] text-white placeholder-[#3A4E6E] transition-colors duration-200 focus:border-[#FF6A3D] focus:outline-none focus:ring-2 focus:ring-[#FF6A3D]/25",
                          },
                          {
                            name: "email",
                            label: "Email",
                            type: "email",
                            placeholder: "you@yourbusiness.com",
                            required: true,
                            className: "w-full rounded-lg border border-[#1A2A44] bg-[#060B14] px-4 py-3 text-[15px] text-white placeholder-[#3A4E6E] transition-colors duration-200 focus:border-[#FF6A3D] focus:outline-none focus:ring-2 focus:ring-[#FF6A3D]/25",
                          },
                          {
                            name: "message",
                            label: "What are you looking to build?",
                            type: "textarea",
                            placeholder: "A few lines about your business and what you need",
                            required: true,
                            rows: 4,
                            className: "w-full resize-none rounded-lg border border-[#1A2A44] bg-[#060B14] px-4 py-3 text-[15px] text-white placeholder-[#3A4E6E] transition-colors duration-200 focus:border-[#FF6A3D] focus:outline-none focus:ring-2 focus:ring-[#FF6A3D]/25",
                          },
                        ]}
                        submitButtonText="Send message"
                        successMessage="Thanks! We'll be in touch within one working day."
                        className="max-w-none space-y-5"
                        showButtonGlow={false}
                        buttonWrapperClassName="relative w-full pt-1"
                        // Button's size="lg" ships rounded-md/h-11; ! forces ours to win.
                        buttonClassName="relative z-10 !h-12 w-full !rounded-full bg-[#FF6A3D] text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#FF8055] disabled:opacity-50"
                        labelClassName="dp-mono mb-2 block text-[10.5px] uppercase tracking-[0.14em] text-[#7E93B2]"
                      />
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </section>
        </main>

        <Footer />

        {/* Sticky mobile CTA — the whole page is one tap from starting */}
        <AnimatePresence>
          {showStickyCta && (
            <motion.div
              className="fixed inset-x-0 bottom-0 z-40 border-t border-[#16233A] bg-[#060B14]/95 px-4 py-3 backdrop-blur-xl md:hidden"
              initial={reduce ? false : { y: "100%" }}
              animate={{ y: 0 }}
              exit={reduce ? undefined : { y: "100%" }}
              transition={{ duration: 0.35, ease: EASE }}
            >
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="dp-mono text-[10px] uppercase tracking-[0.14em] text-[#5C749B]">
                    Free 15-min call
                  </p>
                  <p className="truncate text-[13px] text-[#B6C6DE]">Live in under 2 weeks</p>
                </div>
                <a
                  href="/appointment"
                  className="dp-focus inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#FF6A3D] px-5 py-2.5 text-[14px] font-semibold text-white"
                >
                  Book a call <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

export default Websites;
